import React, { createRef, useEffect, useRef, useState } from "react";
import { makeStyles } from "@material-ui/core/styles";
import { Button, CircularProgress, TextField, Typography } from "@material-ui/core";
import { Close } from "@material-ui/icons";
import GoogleIcon from "@mui/icons-material/Google";
import { useTranslation } from "react-i18next";
import { getAuth, GoogleAuthProvider, signInWithPopup } from "firebase/auth";

import CountryDialCodeSelect from "~/components/common/CountryDialCodeSelect";
import SpinnerLoading from "~/components/common/spinnerLoading/SpinnerLoading";
import { colors } from "~/utils/colors";
import Utils from "~/helpers/Utils";
import { mixpanelTrackEvent } from "~/utils/mixpanel";
import useInterval from "~/utils/useInterval";
import checkoutServices from "~/services/checkoutServices";
import {
	sendNumber,
	sendSMSCodeTwilio,
	sendSmsTwilio,
	sendTokenFirebase,
	verifyCode,
} from "~/services/loginService";

const RESEND_INTERVAL = 60;

// Marca, através do reload do onSuccess, que o login com Google está sendo concluído
// (a BookingConfirmationScreen mostra a mensagem de autenticação no loading pós-reload).
export const GOOGLE_LOGIN_AUTHENTICATING_KEY = "@prit-web/googleLoginAuthenticating";

const useStyles = makeStyles(() => {
	const { background, brand } = colors;
	return {
		"@keyframes slideUp": {
			from: { transform: "translateY(100%)", opacity: 0 },
			to:   { transform: "translateY(0)",    opacity: 1 },
		},
		overlay: {
			position: "fixed",
			top: 0, left: 0, right: 0, bottom: 0,
			backgroundColor: "rgba(0, 0, 0, 0.5)",
			zIndex: 1299,
		},
		container: {
			position: "fixed",
			bottom: 0, left: 0, right: 0,
			width: "100%",
			maxWidth: 480,
			margin: "0 auto",
			display: "flex",
			flexDirection: "column",
			padding: "28px 24px 40px",
			borderTopLeftRadius: 30,
			borderTopRightRadius: 30,
			boxShadow: "0px -4px 12px rgba(0, 0, 0, 0.2)",
			backgroundColor: background?.default || "#FFFFFF",
			animation: "$slideUp 0.3s ease-out",
			zIndex: 1300,
		},
		closeButton: {
			position: "absolute",
			top: 16, right: 16,
			cursor: "pointer",
			display: "flex",
			alignItems: "center",
			justifyContent: "center",
			width: 32, height: 32,
			borderRadius: "50%",
			backgroundColor: "rgba(0, 0, 0, 0.05)",
		},
		title: {
			fontWeight: 700,
			fontSize: "clamp(18px, 3vw, 22px)",
			textAlign: "center",
			marginBottom: 8,
			color: "#333333",
		},
		subtitle: {
			fontSize: "clamp(14px, 2vw, 16px)",
			textAlign: "center",
			color: "#666666",
			lineHeight: 1.4,
			marginBottom: 24,
		},
		phoneRow: {
			display: "flex",
			alignItems: "center",
			gap: 12,
			marginBottom: 8,
			width: "100%",
		},
		otpRow: {
			display: "flex",
			justifyContent: "space-around",
			marginBottom: 8,
		},
		otpField: {
			width: 38,
		},
		infoBox: {
			backgroundColor: "#FFF8E1",
			border: "1px solid #FFE082",
			borderRadius: 10,
			padding: "10px 14px",
			marginBottom: 20,
			textAlign: "center",
		},
		infoBoxText: {
			fontSize: 13,
			color: "#795548",
			lineHeight: 1.5,
		},
		errorText: {
			color: "#f44336",
			fontSize: 13,
			textAlign: "center",
			marginBottom: 12,
			minHeight: 18,
		},
		button: {
			backgroundColor: brand?.default || "#672E37",
			color: "#fff",
			width: "100%",
			padding: "12px 0",
			borderRadius: 100,
			textTransform: "none",
			fontWeight: "bold",
			fontSize: 15,
			marginTop: 8,
			"&:hover": { backgroundColor: brand?.default || "#672E37" },
			"&:disabled": {
				backgroundColor: "#f5f5f5",
				color: "#999999",
				border: "1px solid #cccccc",
			},
		},
		googleIcon: {
			marginRight: 10,
			fontSize: 20,
		},
		timerText: {
			textAlign: "center",
			marginTop: 14,
			fontSize: 13,
			color: "#999999",
		},
		resendLink: {
			textAlign: "center",
			marginTop: 14,
			fontSize: 14,
			color: "#3689ea",
			cursor: "pointer",
			fontWeight: "bold",
		},
	};
});

/**
 * @param {"sms"|"google"} [loginMethod] - "google" é usado quando o cliente não tem
 *   telefone (auth_code LOGINEMAIL no link de booking).
 * @param {object} [trackingProps] - Propriedades extras do evento de abertura do modal Google.
 */
export default function ModalSmsAuth({ open, onClose, onSuccess, loginMethod = "sms", trackingProps }) {
	const classes = useStyles();
	const { t } = useTranslation();

	const [subStep,         setSubStep]         = useState("phone"); // "phone" | "otp"
	const [country,         setCountry]         = useState("BR");
	const [ddiIndex,        setDdiIndex]        = useState(() => {
		const idx = Utils.ddis.findIndex((d) => d.ddi === "+55");
		return idx !== -1 ? idx : 0;
	});
	const [phone,           setPhone]           = useState("");
	const [validateBy,      setValidateBy]      = useState("twilio"); // "twilio" | "firebase"
	const [verificationId,  setVerificationId]  = useState(null);
	const [loading,         setLoading]         = useState(false);
	const [error,           setError]           = useState("");
	const [resendCounter,   setResendCounter]   = useState(RESEND_INTERVAL);
	const [resendActive,    setResendActive]    = useState(false);
	const [authenticating,  setAuthenticating]  = useState(false); // pós-popup do Google

	const otpRefs = useRef(Array.from({ length: 6 }, () => createRef(null)));
	const openTrackedRef = useRef(false);

	useInterval(
		() => setResendCounter((c) => c - 1),
		subStep === "otp" && resendCounter > 0 ? 1000 : null
	);

	useEffect(() => {
		if (resendCounter === 0) setResendActive(true);
	}, [resendCounter]);

	// Uma vez por abertura do modal Google; o ref reseta quando o modal fecha.
	useEffect(() => {
		if (!open) {
			openTrackedRef.current = false;
			return;
		}
		if (loginMethod !== "google" || openTrackedRef.current) return;
		openTrackedRef.current = true;
		mixpanelTrackEvent("Modal_Login_Email_Lembretes", {
			...trackingProps,
			Path: window.location.pathname,
		});
	}, [open, loginMethod]); // eslint-disable-line react-hooks/exhaustive-deps

	useEffect(() => {
		if (open) {
			document.body.style.overflow = "hidden";
		} else {
			document.body.style.overflow = "unset";
			// reset ao fechar
			setSubStep("phone");
			setPhone("");
			setError("");
			setResendCounter(RESEND_INTERVAL);
			setResendActive(false);
			setLoading(false);
			setValidateBy("twilio");
			setVerificationId(null);
			setAuthenticating(false);
		}
		return () => { document.body.style.overflow = "unset"; };
	}, [open]);

	if (!open) return null;

	const currentDdi  = Utils.ddis[ddiIndex];
	const ddi         = currentDdi?.ddi || "+55";
	const rawDigits   = phone.replace(/\D/g, "");
	const fullPhone   = ddi + Utils.removePrefix(currentDdi, rawDigits);

	const handleSendSms = async () => {
		setLoading(true);
		setError("");
		try {
			const via = ddi !== "+55" ? "firebase" : "twilio";
			if (ddi !== "+55") {
				const res = await sendNumber(fullPhone);
				setVerificationId(res.verificationId);
				setValidateBy("firebase");
			} else {
				await sendSmsTwilio(fullPhone);
				setValidateBy("twilio");
			}
			mixpanelTrackEvent("Login_SMS_Lembretes", {
				etapa: "envio",
				status: "OK",
				ddi,
				via,
			});
			setSubStep("otp");
			setResendCounter(RESEND_INTERVAL);
			setResendActive(false);
			setTimeout(() => {
				const first = otpRefs.current[0];
				if (first && first.current) first.current.focus();
			}, 100);
		} catch (err) {
			const errorMessage = err?.response?.data?.message || t("Error sending SMS Please try again");
			mixpanelTrackEvent("Login_SMS_Lembretes", {
				etapa: "envio",
				status: "ERRO",
				ddi,
				error_message: errorMessage,
			});
			setError(errorMessage);
		} finally {
			setLoading(false);
		}
	};

	const handleVerifyOtp = async () => {
		const code = otpRefs.current.map((r) => r.current?.value || "").join("");
		if (code.length < 6) return;
		setLoading(true);
		setError("");
		try {
			if (validateBy === "twilio") {
				const res = await sendSMSCodeTwilio(fullPhone, code);
				await sendTokenFirebase(res.data.message);
			} else {
				await verifyCode({ verificationId }, code);
			}
			mixpanelTrackEvent("Login_SMS_Lembretes", {
				etapa: "verificacao",
				status: "OK",
				ddi,
				via: validateBy,
			});
			if (onSuccess) onSuccess();
		} catch (err) {
			const errorMessage = err?.response?.data?.message || t("Invalid code Please try again");
			mixpanelTrackEvent("Login_SMS_Lembretes", {
				etapa: "verificacao",
				status: "ERRO",
				ddi,
				via: validateBy,
				error_message: errorMessage,
			});
			setError(errorMessage);
		} finally {
			setLoading(false);
		}
	};

	// Login com Google (popup + /me) de buttonsLogin / DialogWhatsAppAuth, sem o desvio
	// para /login/personal-information-number: volta direto para a tela via onSuccess.
	const handleGoogleLogin = async () => {
		setLoading(true);
		setError("");
		try {
			await signInWithPopup(getAuth(), new GoogleAuthProvider());

			// Popup ok: loading cobre /me, localStorage e o reload até a tela aparecer.
			setAuthenticating(true);
			try { sessionStorage.setItem(GOOGLE_LOGIN_AUTHENTICATING_KEY, "1"); } catch (e) {}

			const res = await checkoutServices.me();

			if (res?.data?.id) {
				localStorage.setItem("@prit-web/meData", JSON.stringify(res.data));
				localStorage.setItem("@prit-web/userId", res.data.id);
			}

			mixpanelTrackEvent("Login_Google_Lembretes", { status: "OK" });
			if (onSuccess) onSuccess();
		} catch (err) {
			setAuthenticating(false);
			try { sessionStorage.removeItem(GOOGLE_LOGIN_AUTHENTICATING_KEY); } catch (e) {}
			mixpanelTrackEvent("Login_Google_Lembretes", {
				status: "ERRO",
				error_message: err?.message,
			});
			setError(`${t("Error logging in with Google")}: ${err?.message}`);
		} finally {
			setLoading(false);
		}
	};

	const handleResend = async () => {
		if (!resendActive || loading) return;
		otpRefs.current.forEach((r) => { if (r.current) r.current.value = ""; });
		await handleSendSms();
	};

	const handleOtpChange = (e, index) => {
		if (e.target.value.length > 1) e.target.value = e.target.value.charAt(0);
		if (e.target.value.length >= 1 && index < 5) {
			const next = otpRefs.current[index + 1];
			if (next && next.current) next.current.focus();
		}
	};

	const handleOtpKeyDown = (e, index) => {
		if (e.key === "Backspace" && e.target.value === "" && index > 0) {
			const prev = otpRefs.current[index - 1];
			if (prev && prev.current) prev.current.focus();
		}
	};

	const otpComplete = otpRefs.current.every((r) => r.current?.value?.length === 1);
	const phoneDigits = rawDigits;

	if (authenticating) {
		return <SpinnerLoading loading slowMessage={t("We are completing your authentication")} />;
	}

	return (
		<>
			<div className={classes.overlay} />

			<div className={classes.container}>
				{onClose && (
					<div className={classes.closeButton} onClick={onClose}>
						<Close style={{ fontSize: 20, color: "#666" }} />
					</div>
				)}

				{loginMethod === "google" && (
					<>
						<Typography className={classes.title}>
							{t("Confirm your account")}
						</Typography>
						<div className={classes.infoBox}>
							<Typography className={classes.infoBoxText}>
								{t("To access your booking please log in with your Google account")}
							</Typography>
						</div>
						<Typography className={classes.subtitle}>
							{t("Use the Google account linked to your email")}
						</Typography>

						<Typography className={classes.errorText}>{error}</Typography>

						<Button
							className={classes.button}
							disabled={loading}
							onClick={handleGoogleLogin}
						>
							{loading
								? <CircularProgress size={20} style={{ color: "#fff" }} />
								: <><GoogleIcon className={classes.googleIcon} />{t("Continue Google")}</>
							}
						</Button>
					</>
				)}

				{loginMethod === "sms" && subStep === "phone" && (
					<>
						<Typography className={classes.title}>
							{t("Confirm your phone")}
						</Typography>
						<div className={classes.infoBox}>
							<Typography className={classes.infoBoxText}>
								{t("Your session has expired To continue please log in via SMS")}
							</Typography>
						</div>
						<Typography className={classes.subtitle}>
							{t("We will send you a code via SMS to validate your phone number")}
						</Typography>

						<div className={classes.phoneRow}>
							<CountryDialCodeSelect
								valueISO2={country}
								onChange={({ dialCode, iso2 }) => {
									setCountry(iso2);
									const idx = Utils.ddis.findIndex((d) => d.ddi === dialCode);
									setDdiIndex(idx !== -1 ? idx : 0);
									setPhone("");
								}}
								width="42%"
							/>
							<TextField
								value={phone}
								onChange={(e) => {
									const ev = { target: { value: e.target.value } };
									const prefix = currentDdi?.prefix || Utils.ddis[0]?.prefix || "BRA";
									Utils.telephoneMask(ev, prefix);
									setPhone(ev.target.value);
								}}
								placeholder={t("Enter your phone")}
								fullWidth
								type="tel"
								inputProps={{ maxLength: currentDdi?.pattern?.maxLength || 15 }}
								style={{ marginLeft: 10 }}
							/>
						</div>

						<Typography className={classes.errorText}>{error}</Typography>

						<Button
							className={classes.button}
							disabled={phoneDigits.length < 8 || loading}
							onClick={handleSendSms}
						>
							{loading
								? <CircularProgress size={20} style={{ color: "#fff" }} />
								: t("go")
							}
						</Button>
					</>
				)}

				{loginMethod === "sms" && subStep === "otp" && (
					<>
						<Typography className={classes.title}>
							{t("Enter the confirmation code")}
						</Typography>
						<Typography className={classes.subtitle}>
							{t("Your number")} {ddi} <strong>{phone}</strong>
						</Typography>

						<div className={classes.otpRow}>
							{otpRefs.current.map((ref, i) => (
								<TextField
									key={i}
									className={classes.otpField}
									variant="outlined"
									type="number"
									inputRef={ref}
									inputProps={{ maxLength: 1 }}
									onChange={(e) => handleOtpChange(e, i)}
									onKeyDown={(e) => handleOtpKeyDown(e, i)}
									onKeyPress={(e) => {
										if (e.key === "Enter" && i === 5) handleVerifyOtp();
									}}
								/>
							))}
						</div>

						<Typography className={classes.errorText}>{error}</Typography>

						<Button
							className={classes.button}
							disabled={!otpComplete || loading}
							onClick={handleVerifyOtp}
						>
							{loading
								? <CircularProgress size={20} style={{ color: "#fff" }} />
								: t("go")
							}
						</Button>

						{resendActive ? (
							<Typography className={classes.resendLink} onClick={handleResend}>
								{t("Resend code")}
							</Typography>
						) : (
							<Typography className={classes.timerText}>
								{t("Resend on")} {resendCounter}s
							</Typography>
						)}
					</>
				)}

				{/* Necessário para o Firebase phone auth */}
				<div id="recaptcha-container" />
			</div>
		</>
	);
}
